import { t } from '../i18n';

export type KeyEvent = { type: 'letter'; value: string } | { type: 'enter' } | { type: 'delete' };
export type KeyState = 'correct' | 'present' | 'absent';

const LAYOUTS: Record<'ru' | 'en', string[][]> = {
  ru: [
    ['Й','Ц','У','К','Е','Н','Г','Ш','Щ','З','Х','Ъ'],
    ['Ф','Ы','В','А','П','Р','О','Л','Д','Ж','Э'],
    ['ENTER','Я','Ч','С','М','И','Т','Ь','Б','Ю','DEL']
  ],
  en: [
    ['Q','W','E','R','T','Y','U','I','O','P'],
    ['A','S','D','F','G','H','J','K','L'],
    ['ENTER','Z','X','C','V','B','N','M','DEL']
  ]
};

export class Keyboard {
  private root: HTMLDivElement;
  private listeners: ((e: KeyEvent) => void)[] = [];
  private letterState: Map<string, KeyState> = new Map();
  private buttons: Map<string, HTMLButtonElement> = new Map();

  constructor(root: HTMLDivElement) {
    this.root = root;
  }

  render(lang: 'ru' | 'en' = 'ru') {
    this.root.innerHTML = '';
    this.buttons.clear();
    const layout = LAYOUTS[lang];
    for (const row of layout) {
      const rowEl = document.createElement('div');
      rowEl.className = 'kbd-row';
      for (const key of row) {
        const btn = document.createElement('button');
        btn.className = 'kbd-key';
        if (key === 'ENTER') {
          btn.classList.add('wide');
          btn.textContent = t('btn.enter');
          btn.addEventListener('click', () => this.emit({ type: 'enter' }));
        } else if (key === 'DEL') {
          btn.classList.add('wide');
          btn.textContent = '⌫';
          btn.addEventListener('click', () => this.emit({ type: 'delete' }));
        } else {
          btn.textContent = key;
          btn.dataset.letter = key;
          btn.addEventListener('click', () => this.emit({ type: 'letter', value: key }));
          this.buttons.set(key, btn);
        }
        rowEl.appendChild(btn);
      }
      this.root.appendChild(rowEl);
    }
    this.repaintStates();
  }

  on(cb: (e: KeyEvent) => void) { this.listeners.push(cb); }

  private emit(e: KeyEvent) { for (const l of this.listeners) l(e); }

  setLetterState(letter: string, state: KeyState) {
    const prev = this.letterState.get(letter);
    // Upgrade state: absent < present < correct
    const rank = { absent: 0, present: 1, correct: 2 } as const;
    if (!prev || rank[state] > rank[prev]) {
      this.letterState.set(letter, state);
      this.repaintKey(letter);
    }
  }

  private repaintKey(letter: string) {
    const btn = this.buttons.get(letter);
    if (!btn) return;
    btn.classList.remove('state-correct', 'state-present', 'state-absent');
    const st = this.letterState.get(letter);
    if (st) btn.classList.add(`state-${st}`);
  }

  private repaintStates() {
    for (const letter of this.buttons.keys()) this.repaintKey(letter);
  }
}
